import{actionResourceVersionMatches}from'../scheduled-gift-preparation';
import {actionInput,fieldValue,validateField} from "../transport";
import React, { useState, useRef, useEffect, type ReactNode } from "react";
import type { View, State, Card, Money, Action, Operation } from "../transport";
import type { LaunchLocale } from "../../../locales/src/locales";
import {
  postPurchaseCopy,
  postPurchaseScreens,
  type PostPurchaseScreen,
} from "./copy";
const isScreen = (s: string): s is PostPurchaseScreen =>
  (postPurchaseScreens as readonly string[]).includes(s);
/** UI affordances only, never a permission grant. Privileged adjudication is never a buyer affordance. O01 can render a
 * source-issued reviewer action only under the current server session/ACL; the
 * workspace itself cannot confer support, reviewer or financial authority. */
const capabilities: Record<PostPurchaseScreen, readonly Operation[]> = {
  B09: ["digital.manifest.map", "digital.proof.manage", "digital.grant.manage", "gifting.slip", "gifting.teaser", "gifting.schedule", "gifting.schedule.media"],
  B10: ["identity.claim"],
  B11: ["custom.accept", "custom.renew", "custom.preserve"],
  B12: [
    "conversation.send",
    "conversation.draft",
    "conversation.report",
    "conversation.preference",
    "conversation.block",
    "conversation.schedule",
    "conversation.registerAttachment",
  ],
  B13: ["aftersales.eligibility", "case.submit", "case.refund", "aftersales.allocate", "aftersales.refund"],
  B14: ["case.submit", "case.refund", "aftersales.allocate", "aftersales.refund"],
  B15: ["purchase.review"],
  S06: ["digital.proof.manage", "custom.version", "custom.preserve", "fulfillment.reserve"],
  S07: [
    "fulfillment.ship",
    "fulfillment.quote",
    "fulfillment.label",
    "fulfillment.labelRefund",
  ],
  S09: [
    "aftersales.eligibility",
    "aftersales.refund",
    "aftersales.allocate",
    "delegation.prepare",
    "delegation.confirm",
  ],
  S11: [
    "conversation.send",
    "conversation.draft",
    "conversation.report",
    "conversation.preference",
    "conversation.block",
    "conversation.schedule",
    "conversation.registerAttachment",
  ],
  O01: [
    "case.submit",
    "case.review",
    "conversation.send",
    "conversation.report",
  ],
};
export function workflowActions(screen: string, view: View): Action[] {
  return isScreen(screen)
    ? view.actions.filter(
        (a) =>
          capabilities[screen].includes(a.id) &&
          !isCaseProposal(a) &&
          !!a.instanceId &&
          !!a.resource?.id &&
          Number.isSafeInteger(a.resource.version) &&
          a.resource.version > 0 &&
          actionResourceVersionMatches(a),
      )
    : [];
}
export function findMessages(
  messages: NonNullable<View["messages"]> | undefined,
  query: string,
) {
  const q = query.normalize("NFC").toLocaleLowerCase();
  return (messages ?? []).filter(
    (m) =>
      !q ||
      [m.text, m.original ?? ""].some((t) =>
        t.normalize("NFC").toLocaleLowerCase().includes(q),
      ),
  );
}
const extra = {
  en: {
    search: "Find messages",
    results: "Matching messages",
    actions: "Available actions",
    files: "Files",
    download: "Download",
    next: "Next page",
    unavailable: "There’s nothing you can do here right now.",
    loading: "Loading…",
    denied: "You don’t have access to this page.",
    error: "We couldn’t load this page. Please refresh to try again.",
    stale: "Some details have changed. Refresh before you continue.",
    returnPolicy: "Return policy and the item you bought",
    review: "Review options for your purchase",
    work: "Production instructions and what was ordered",
    shipping: "Shipping, route and label",
    support: "Support details and shared evidence",
  },
  es: {
    search: "Buscar mensajes",
    results: "Mensajes coincidentes",
    actions: "Acciones disponibles",
    files: "Archivos",
    download: "Descargar",
    next: "Página siguiente",
    unavailable: "Ahora mismo no hay nada que puedas hacer aquí.",
    loading: "Cargando…",
    denied: "No tienes acceso a esta página.",
    error: "No pudimos cargar esta página. Actualiza para intentarlo de nuevo.",
    stale: "Algunos detalles han cambiado. Actualiza antes de continuar.",
    returnPolicy: "Política de devolución y el artículo que compraste",
    review: "Opciones de reseña de tu compra",
    work: "Instrucciones de producción y lo que se pidió",
    shipping: "Envío, ruta y etiqueta",
    support: "Detalles de soporte y pruebas compartidas",
  },
  fr: {
    search: "Rechercher des messages",
    results: "Messages correspondants",
    actions: "Actions disponibles",
    files: "Fichiers",
    download: "Télécharger",
    next: "Page suivante",
    unavailable: "Vous ne pouvez rien faire ici pour le moment.",
    loading: "Chargement…",
    denied: "Vous n’avez pas accès à cette page.",
    error: "Impossible de charger cette page. Actualisez pour réessayer.",
    stale: "Certaines informations ont changé. Actualisez avant de continuer.",
    returnPolicy: "Politique de retour et article acheté",
    review: "Options d’avis sur votre achat",
    work: "Instructions de production et contenu de la commande",
    shipping: "Expédition, itinéraire et étiquette",
    support: "Informations d’assistance et preuves partagées",
  },
  de: {
    search: "Nachrichten suchen",
    results: "Passende Nachrichten",
    actions: "Verfügbare Aktionen",
    files: "Dateien",
    download: "Herunterladen",
    next: "Nächste Seite",
    unavailable: "Hier kannst du gerade nichts tun.",
    loading: "Wird geladen …",
    denied: "Du hast keinen Zugriff auf diese Seite.",
    error: "Diese Seite konnte nicht geladen werden. Lade sie neu, um es erneut zu versuchen.",
    stale: "Einige Angaben haben sich geändert. Lade neu, bevor du fortfährst.",
    returnPolicy: "Rückgaberegeln und gekaufter Artikel",
    review: "Bewertungsoptionen für deinen Kauf",
    work: "Produktionsanweisungen und Bestellumfang",
    shipping: "Versand, Route und Etikett",
    support: "Support-Angaben und geteilte Nachweise",
  },
  ja: {
    search: "メッセージを検索",
    results: "一致するメッセージ",
    actions: "利用できる操作",
    files: "ファイル",
    download: "ダウンロード",
    next: "次のページ",
    unavailable: "現在ここで行える操作はありません。",
    loading: "読み込み中…",
    denied: "このページへのアクセス権がありません。",
    error: "このページを読み込めませんでした。再読み込みしてもう一度お試しください。",
    stale: "一部の情報が変更されました。続行する前に再読み込みしてください。",
    returnPolicy: "返品ポリシーと購入した商品",
    review: "購入のレビューについて",
    work: "制作指示と注文内容",
    shipping: "発送・配送経路・ラベル",
    support: "サポート情報と共有された資料",
  },
} satisfies Record<LaunchLocale, Record<string, string>>;
export type WorkspaceProps = {
  screen: string;
  view: View;
  state: State;
  locale: LaunchLocale;
  identityEpoch: number;
  renderCard: (card: Card) => ReactNode;
  renderMoney: (m: Money) => ReactNode;
  renderTime: (instant: string) => ReactNode;
  renderFinancialState: (state: string) => ReactNode;
  renderActions: (actions: readonly Action[]) => ReactNode;
  onPrepareCaseProposal?: (action: Action,input: Record<string,unknown>) => Promise<{proposalId:string}>;
  onDownload?: (
    opaqueId: string,
    kind?: "digital" | "attachment",
  ) => Promise<void>;
  onReadFailure?: (error: unknown) => void;
  renderNext?: (actualSourceNext: string) => ReactNode;
};
type Context = WorkspaceProps & { screen: PostPurchaseScreen };
function Facts({ view, locale }: Context) {
  const c = postPurchaseCopy[locale];
  return view.facts?.length ? (
    <section className="postpurchase-facts">
      <h3>{c.facts}</h3>
      <dl>
        {view.facts.map((f, i) => (
          <React.Fragment key={i}>
            <dt dir="auto">{f.label}</dt>
            <dd>
              <bdi dir="auto">{f.value}</bdi>
            </dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  ) : null;
}
function Items(p: Context) {
  return p.view.items.length ? (
    <section>
      <h3>{postPurchaseCopy[p.locale].items}</h3>
      <div className="product-grid">{p.view.items.map(p.renderCard)}</div>
    </section>
  ) : null;
}
function Amounts(p: Context) {
  return p.view.totals?.length ? (
    <section className="postpurchase-amounts">
      <h3>{postPurchaseCopy[p.locale].totals}</h3>
      <dl>
        {p.view.totals.map((x, i) => (
          <React.Fragment key={i}>
            <dt dir="auto">{x.label}</dt>
            <dd>{p.renderMoney(x.money)}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  ) : null;
}
function Timeline(p: Context) {
  return p.view.timeline?.length ? (
    <section>
      <h3>{postPurchaseCopy[p.locale].timeline}</h3>
      <ol className="timeline">
        {p.view.timeline.map((t) => (
          <li key={t.id}>
            <strong dir="auto">{t.label}</strong>
            <p dir="auto">{p.renderFinancialState(t.state)}</p>
            <time dateTime={t.at}>{p.renderTime(t.at)}</time>
          </li>
        ))}
      </ol>
    </section>
  ) : null;
}
function Changes(p: Context) {
  const c = postPurchaseCopy[p.locale];
  return p.view.changes?.length ? (
    <section className="postpurchase-changes">
      <h3>{c.changes}</h3>
      <ol>
        {p.view.changes.map((x, i) => (
          <li key={i}>
            <h4 dir="auto">{x.field}</h4>
            <dl>
              <dt>{c.before}</dt>
              <dd dir="auto">
                <del>{x.before}</del>
              </dd>
              <dt>{c.after}</dt>
              <dd dir="auto">
                <ins>{x.after}</ins>
              </dd>
            </dl>
          </li>
        ))}
      </ol>
    </section>
  ) : null;
}
function Receipt(p: Context) {
  const c = postPurchaseCopy[p.locale],
    r = p.view.receipt;
  const valid =
    r &&
    r.id.trim() &&
    r.outcome === "ok" &&
    Number.isSafeInteger(r.version) &&
    r.version > 0 &&
    Number.isFinite(Date.parse(r.committedAt));
  return valid ? (
    <section data-committed-receipt={r.id}>
      <h3>{c.receipt}</h3>
      <dl>
        <dt>{c.reference}</dt>
        <dd>
          <bdi>{r.id}</bdi>
        </dd>
        <dt>{c.version}</dt>
        <dd>
          <bdi>{r.version}</bdi>
        </dd>
        <dt>{c.committed}</dt>
        <dd>
          <time dateTime={r.committedAt}>{p.renderTime(r.committedAt)}</time>
        </dd>
      </dl>
      <p>{c.settlement}</p>
    </section>
  ) : p.screen === "B09" ? (
    <p className="notice">{c.noReceipt}</p>
  ) : null;
}
function Conversation(p: Context) {
  const [query, setQuery] = useState(""),
    c = postPurchaseCopy[p.locale],
    t = extra[p.locale],
    messages = findMessages(p.view.messages, query);
  return (
    <section>
      <h3>{c.messages}</h3>
      <label>
        {t.search}
        <input
          type="search"
          value={query}
          maxLength={200}
          onChange={(e) => setQuery(e.currentTarget.value)}
        />
      </label>
      <p role="status" aria-live="polite">
        {t.results}: {messages.length}
      </p>
      <ol className="messages">
        {messages.map((m) => (
          <li key={m.id}>
            <p className="postpurchase-message" dir="auto">
              {m.text}
            </p>
            {m.original && (
              <details>
                <summary>{c.original}</summary>
                <p className="postpurchase-message" dir="auto">
                  {m.original}
                </p>
              </details>
            )}
            {m.attribution && (
              <p dir="auto">
                {c.attribution}: {m.attribution}
              </p>
            )}
            <time dateTime={m.sentAt}>{p.renderTime(m.sentAt)}</time>
          </li>
        ))}
      </ol>
    </section>
  );
}
function Files(p: Context) {
  const [busy, setBusy] = useState<string>(),
    epoch = useRef(p.identityEpoch),
    mounted = useRef(true);
  epoch.current = p.identityEpoch;
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return p.view.downloads?.length ? (
    <section>
      <h3>{extra[p.locale].files}</h3>
      {p.view.downloads.map((d) => {
        const expired =
            !Number.isFinite(Date.parse(d.expiresAt)) ||
            Date.parse(d.expiresAt) <= Date.now(),
          disabled = expired || !p.onDownload || !!busy || p.state === "stale";
        return (
          <article key={d.id}>
            <h4 dir="auto">{d.label}</h4>
            <time dateTime={d.expiresAt}>{p.renderTime(d.expiresAt)}</time>
            <button
              type="button"
              disabled={disabled}
              onClick={async () => {
                if (disabled) return;
                const current = p.identityEpoch;
                setBusy(d.id);
                try {
                  await p.onDownload!(d.id, d.kind);
                } catch (error) {
                  if (mounted.current && current === epoch.current)
                    p.onReadFailure?.(error);
                } finally {
                  if (mounted.current && current === epoch.current)
                    setBusy(undefined);
                }
              }}
            >
              {extra[p.locale].download}
            </button>
          </article>
        );
      })}
    </section>
  ) : null;
}
export function PurchaseReceipt(p: Context) {
  return (
    <>
      <Receipt {...p} />
      <Facts {...p} />
      <Items {...p} />
      <Amounts {...p} />
      <Timeline {...p} />
      <Files {...p} />
    </>
  );
}
export function OrderHistory(p: Context) {
  return (
    <>
      <Items {...p} />
      <Facts {...p} />
      <Amounts {...p} />
      <Timeline {...p} />
      {p.view.next && p.renderNext?.(p.view.next)}
    </>
  );
}
export function CustomProof(p: Context) {
  return (
    <>
      <Changes {...p} />
      <Facts {...p} />
      <Amounts {...p} />
      <Items {...p} />
      <Timeline {...p} />
      <Files {...p} />
    </>
  );
}
export function MessageThread(p: Context) {
  return (
    <>
      <Items {...p} />
      <Conversation {...p} />
      <Facts {...p} />
      <Files {...p} />
      <Timeline {...p} />
    </>
  );
}
export const isCaseProposal = (a: Action) => a.id === "aftersales.allocate" && !!a.resource && a.input.caseId === a.resource.id;
const validProposalInput = (input: Record<string, unknown>, caseId: string) => {
 const money = (m: any) => m && /^[A-Z]{3}$/.test(m.currency) && typeof m.minor === "string" && /^(0|[1-9][0-9]*)$/.test(m.minor);
 const lines = input.lines as any[] | undefined;
 return input.caseId === caseId && Array.isArray(lines) && lines.length === 1 && lines.every(l => typeof l.lineId === "string" && l.lineId.length > 0 && Number.isSafeInteger(l.quantity) && l.quantity > 0 && money(l.amount)) && [input.shipping,input.tax,input.credit].every(money) && [input.shipping,input.tax,input.credit].every((m:any)=>m.currency===lines[0].amount.currency) && typeof input.reasonCode === "string" && input.reasonCode.length > 0;
};
const validCaseProposal = (a: Action) => isCaseProposal(a) && !!a.instanceId && !!a.resource && Number.isSafeInteger(a.resource.version) && a.resource.version > 0 && a.expectedVersion === a.resource.version && Array.isArray(a.fields) && validProposalInput(a.input,a.resource.id);
const proposalCopy = {
 en: ["Refund proposal", "Review the proposal", "Send for a second review", "Back to edit", "Proposal sent for a second review. No refund has been made yet.", "This proposal is no longer available. Refresh before continuing."],
 es: ["Propuesta de reembolso", "Revisar la propuesta", "Enviar a una segunda revisión", "Volver a editar", "Propuesta enviada a una segunda revisión. Todavía no se ha hecho ningún reembolso.", "Esta propuesta ya no está disponible. Actualiza antes de continuar."],
 fr: ["Proposition de remboursement", "Vérifier la proposition", "Envoyer pour une seconde vérification", "Modifier", "Proposition envoyée pour une seconde vérification. Aucun remboursement n’a encore été effectué.", "Cette proposition n’est plus disponible. Actualisez avant de continuer."],
 de: ["Erstattungsvorschlag", "Vorschlag prüfen", "Zur zweiten Prüfung senden", "Zurück zur Bearbeitung", "Vorschlag zur zweiten Prüfung gesendet. Es wurde noch nichts erstattet.", "Dieser Vorschlag ist nicht mehr verfügbar. Lade neu, bevor du fortfährst."],
 ja: ["返金の提案", "提案を確認", "2人目の確認に送信", "編集に戻る", "2人目の確認に送信しました。まだ返金は行われていません。", "この提案は利用できなくなりました。続行する前に再読み込みしてください。"],
} satisfies Record<LaunchLocale, readonly string[]>;
function CaseProposal(p: Context & {action: Action}) {
 const [drafts,setDrafts]=useState<Record<string,unknown>>({}),[review,setReview]=useState<Record<string,unknown>|null>(null),[busy,setBusy]=useState(false),[submitted,setSubmitted]=useState(false),[failed,setFailed]=useState(false);
 const mounted=useRef(true),inFlight=useRef(false),epoch=useRef(p.identityEpoch);epoch.current=p.identityEpoch;
 useEffect(()=>{mounted.current=true;return()=>{mounted.current=false}},[]);
 const text=proposalCopy[p.locale],a=p.action,disabled=busy||!p.onPrepareCaseProposal||!["ready","success"].includes(p.state)||!a.expiresAt||!Number.isFinite(Date.parse(a.expiresAt))||Date.parse(a.expiresAt)<=Date.now();
 const fields=a.fields??[];
 return <section className="case-proposal" aria-label={text[0]}><h3>{text[0]}</h3>{submitted?<p role="status">{text[4]}</p>:<form onSubmit={e=>{e.preventDefault();if(disabled)return;if(fields.some(f=>!validateField(f,fieldValue(a,f,drafts)))||!validProposalInput(actionInput(a,drafts),a.resource!.id)){setFailed(true);return}setFailed(false);setReview(actionInput(a,drafts))}}>
 <p><bdi>{a.resource!.id}</bdi> · {a.resource!.version}</p>
 {review?<><dl>{[...((review.lines as any[])??[]).map(l=>({label:l.lineId,money:l.amount})),...(["shipping","tax","credit"] as const).map(k=>({label:k,money:review[k]}))].map((x,i)=><React.Fragment key={i}><dt dir="auto">{x.label}</dt><dd>{p.renderMoney(x.money as Money)}</dd></React.Fragment>)}</dl><p dir="auto">{String(review.reasonCode??"")}</p><button type="button" disabled={busy} onClick={()=>setReview(null)}>{text[3]}</button><button type="button" disabled={disabled} onClick={async()=>{if(disabled||!review||inFlight.current)return;inFlight.current=true;const current=p.identityEpoch;setBusy(true);try{await p.onPrepareCaseProposal!(a,review);if(mounted.current&&current===epoch.current){setSubmitted(true);setReview(null);setDrafts({})}}catch(error){if(mounted.current&&current===epoch.current){setFailed(true);p.onReadFailure?.(error)}}finally{inFlight.current=false;if(mounted.current&&current===epoch.current)setBusy(false)}}}>{text[2]}</button></>:<>{fields.map((f,i)=><label key={i}>{f.label}<input type={f.type==="number"?"number":"text"} value={String(fieldValue(a,f,drafts)??"")} required={f.required} min={f.min} max={f.max} maxLength={2000} disabled={disabled} onChange={e=>setDrafts({...drafts,[f.name]:e.currentTarget.value})}/></label>)}<button disabled={disabled}>{text[1]}</button></>}
 </form>}{failed&&<p role="alert">{text[5]}</p>}</section>;
}
export function ReturnCase(p: Context) {
  return (
    <>
      <h3>
        {p.screen === "B15"
          ? extra[p.locale].review
          : extra[p.locale].returnPolicy}
      </h3>
      {["B13","B14"].includes(p.screen) && !["loading","denied","error","stale","pending","empty"].includes(p.state) && p.view.actions.filter(validCaseProposal).map(a=><CaseProposal key={p.identityEpoch+":"+p.view.version+":"+a.instanceId} {...p} action={a}/>)}
      <Facts {...p} />
      <Items {...p} />
      <Changes {...p} />
      <Amounts {...p} />
      <Timeline {...p} />
      <Files {...p} />
      <Receipt {...p} />
    </>
  );
}
export function ProductionDesk(p: Context) {
  return (
    <>
      <h3>{extra[p.locale].work}</h3>
      <Items {...p} />
      <Changes {...p} />
      <Facts {...p} />
      <Amounts {...p} />
      <Timeline {...p} />
      <Files {...p} />
    </>
  );
}
export function ShipmentDesk(p: Context) {
  return (
    <>
      <h3>{extra[p.locale].shipping}</h3>
      <Facts {...p} />
      <Items {...p} />
      <Amounts {...p} />
      <Changes {...p} />
      <Timeline {...p} />
      <Files {...p} />
    </>
  );
}
export function SupportDesk(p: Context) {
  return (
    <>
      <h3>{extra[p.locale].support}</h3>
      <Facts {...p} />
      <Conversation {...p} />
      <Items {...p} />
      <Timeline {...p} />
      <Files {...p} />
    </>
  );
}
/** Existing ResourceActions (single instance) owns original typed field paths,
 * draft memory, uploads, prepare→human review, original Command and Pending.
 * This component never constructs a Command, proof, private URL or resource. */
export function PostPurchaseWorkspace(props: WorkspaceProps) {
  if (!isScreen(props.screen)) return null;
  const p = props as Context,
    c = postPurchaseCopy[p.locale],
    t = extra[p.locale],
    idx = postPurchaseScreens.indexOf(p.screen);
  const privateAllowed = !["loading", "denied", "error"].includes(p.state);
  const expired =
    p.view.expiresAt && Date.parse(p.view.expiresAt) <= Date.now();
  const actions =
    privateAllowed && !expired && !["stale", "empty"].includes(p.state)
      ? workflowActions(p.screen, p.view)
      : [];
  return (
    <section
      className={"postpurchase-workspace postpurchase-" + p.screen}
      data-workspace={p.screen}
      data-workspace-state={p.state}
      aria-busy={p.state === "loading"}
    >
      <header>
        <h2>{c.titles[idx]}</h2>
        <p>{c.context[idx]}</p>
      </header>
      {!privateAllowed ? (
        <p role={p.state === "error" ? "alert" : "status"}>
          {p.state === "loading"
            ? t.loading
            : p.state === "denied"
              ? t.denied
              : t.error}
        </p>
      ) : (
        <>
          {p.state === "pending" && (
            <p role="status" className="notice">
              {c.pending}
            </p>
          )}
          {(p.state === "stale" || expired) && (
            <p role="status" className="notice">
              {t.stale}
            </p>
          )}
          {p.screen === "B09" ? (
            <PurchaseReceipt {...p} />
          ) : p.screen === "B10" ? (
            <OrderHistory {...p} />
          ) : p.screen === "B11" ? (
            <CustomProof {...p} />
          ) : p.screen === "B12" || p.screen === "S11" ? (
            <MessageThread {...p} />
          ) : p.screen === "S06" ? (
            <ProductionDesk {...p} />
          ) : p.screen === "S07" ? (
            <ShipmentDesk {...p} />
          ) : p.screen === "O01" ? (
            <SupportDesk {...p} />
          ) : (
            <ReturnCase {...p} />
          )}
          <section className="postpurchase-actions">
            <h3>{t.actions}</h3>
            {actions.length ? p.renderActions(actions) : <p>{t.unavailable}</p>}
          </section>
          {p.state === "empty" && <p>{c.empty}</p>}
        </>
      )}
    </section>
  );
}
